import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { DEFAULT_CONTENT } from "../contentDefaults";
import { assetUrl } from "../adminApi";

const DEFAULT_AUTOPLAY_MS = 5000;

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const { t, loc, lang } = useI18n();
  const { content, collections } = useSiteData();

  const hero = content.hero || {};
  const heroItems = (hero.items || []).filter((s) => s && s.visible !== false && (s.image || s.title));
  const slides = heroItems.length
    ? heroItems
    : content.home?.slides?.length
    ? content.home.slides
    : DEFAULT_CONTENT.home.slides;
  const count = slides.length;
  const AUTOPLAY_MS = Number(hero.autoplay) > 0 ? Number(hero.autoplay) * 1000 : DEFAULT_AUTOPLAY_MS;

  const go = useCallback(
    (next, direction = 1) => {
      setDir(direction);
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  const next = useCallback(() => go(index + 1, 1), [go, index]);
  const prev = useCallback(() => go(index - 1, -1), [go, index]);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setTimeout(() => go(index + 1, 1), AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused, go, AUTOPLAY_MS]);

  useEffect(() => {
    slides.forEach((s) => {
      if (!s.image) return;
      const img = new Image();
      img.src = assetUrl(s.image);
    });
  }, [slides]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const slide = slides[Math.min(index, count - 1)];
  const meta = collections.find((c) => c.key === slide.id) || {};

  return (
    <section className="relative h-[100svh] bg-[#EFECE6] overflow-hidden select-none" data-testid="hero-slider">
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide.image || slide.id}
            src={assetUrl(slide.image)}
            alt={`${slide.title} panel composition`}
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.07 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />
        </AnimatePresence>
      </div>

      <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA]/45 to-[#FAFAFA]/10 lg:bg-gradient-to-r lg:from-[#FAFAFA] lg:via-[#FAFAFA]/70 lg:to-transparent" />
      <div className="absolute inset-x-0 top-0 h-[140px] z-10 pointer-events-none bg-gradient-to-b from-[#FAFAFA]/85 to-transparent" />

      <motion.div
        className="relative z-20 h-full flex flex-col justify-end cursor-grab active:cursor-grabbing"
        drag="x"
        dragElastic={0.14}
        dragConstraints={{ left: 0, right: 0 }}
        onDragStart={() => setPaused(true)}
        onDragEnd={(e, info) => {
          setPaused(false);
          if (info.offset.x < -70) next();
          else if (info.offset.x > 70) prev();
        }}
      >
        <div className="shell w-full pb-8 lg:pb-12">
          <div className="max-w-[720px]">
            <p className="mono text-[9px] text-[#141414]/70 mb-4 hidden sm:block">{t("hero.tag")}</p>
            <AnimatePresence mode="wait">
              <motion.div
                key={(slide.id || slide.title || "") + lang}
                initial={{ opacity: 0, x: dir * 44 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -28 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="mono text-[9.5px] text-[#8C7862]">{loc(slide.kicker)}</p>
                <h1 className="display text-[#141414] text-[clamp(3.5rem,20vw,5.5rem)] sm:text-[clamp(4.5rem,15vw,8rem)] lg:text-[clamp(5.5rem,10vw,11rem)] leading-[0.84] mt-3 break-words">
                  {slide.title}
                </h1>
                <p className="text-[15px] lg:text-[17px] leading-relaxed text-[#141414]/75 max-w-[520px] mt-5">
                  {loc(slide.text)}
                </p>
                <div className="mt-6 flex flex-wrap gap-x-7 gap-y-2">
                  {[
                    [t("hero.modules"), meta.modules],
                    [t("hero.panel"), meta.panel],
                    [t("hero.layouts"), meta.layouts],
                  ]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <span key={k} className="mono text-[8.5px] text-[#6B675F]">
                        {k.toUpperCase()} <span className="text-[#141414]">{v}</span>
                      </span>
                    ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <Link
              to={slide.link || "/products"}
              data-testid="hero-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#141414] text-[#FAFAFA] px-6 py-4 mono text-[10px] hover:bg-[#2A2A28] transition-colors duration-300"
            >
              {t("cta.explore")} <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>

        <div className="relative border-t border-[#E2E0D8] bg-[#FAFAFA]/80 backdrop-blur-md">
          <div className="shell">
            <div className="flex items-center justify-between gap-6 py-3">
              <div className="hidden sm:flex items-center gap-2">
                <button
                  aria-label="Previous slide"
                  onClick={prev}
                  className="w-9 h-9 border border-[#E2E0D8] flex items-center justify-center text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] transition-colors duration-300"
                >
                  <ArrowLeft size={14} strokeWidth={1.6} />
                </button>
                <button
                  aria-label="Next slide"
                  onClick={next}
                  className="w-9 h-9 border border-[#E2E0D8] flex items-center justify-center text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] transition-colors duration-300"
                >
                  <ArrowRight size={14} strokeWidth={1.6} />
                </button>
              </div>

              <div className="grid gap-2 sm:gap-4 flex-1" style={{ gridTemplateColumns: `repeat(${Math.min(count, 6)}, minmax(0, 1fr))` }}>
                {slides.map((s, i) => {
                  const isActive = i === index;
                  return (
                    <button
                      key={s.id || s.title || i}
                      onClick={() => go(i, i > index ? 1 : -1)}
                      data-testid={`hero-tab-${s.id || i}`}
                      className="group relative text-left pt-3 pb-3 cursor-pointer"
                    >
                      <span
                        className={`mono text-[8px] sm:text-[9px] block transition-colors duration-300 ${
                          isActive ? "text-[#8C7862]" : "text-[#A8A29A] group-hover:text-[#6B675F]"
                        }`}
                      >
                        {s.no}
                      </span>
                      <span
                        className={`display text-[13px] sm:text-[17px] block mt-1 transition-colors duration-300 ${
                          isActive ? "text-[#141414]" : "text-[#141414]/40 group-hover:text-[#141414]/70"
                        }`}
                      >
                        {s.title}
                      </span>
                      <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#E2E0D8]" />
                      {isActive && (
                        <motion.span
                          key={`${s.id}-${index}-${paused}`}
                          className="absolute left-0 bottom-0 h-[2px] bg-[#141414] origin-left"
                          style={{ width: "100%" }}
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: paused ? 0.08 : 1 }}
                          transition={{ duration: paused ? 0.4 : AUTOPLAY_MS / 1000, ease: "linear" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="hidden lg:block mono text-[8.5px] text-[#6B675F] shrink-0">{t("hero.hint")}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
